/* eslint-disable react/no-unknown-property */

/**
 * Browsers that do not support responsive images preload may
 * request the image twice if the image has `priority: true`.
 * They will try to initially preload the `src` image and then,
 * if there is responsive images on `srcSet`, it will request the responsive one
 * See:
 *  - https://caniuse.com/?search=imagesizes
 *  - https://caniuse.com/?search=imagesrcset
 * The only relevant browser that seems to not support these
 * is Safari, which is a good tradeoff.
 * This component aims to have a similar basic API compared to
 * https://nextjs.org/docs/api-reference/next/image so we can transition from it
 * to next/image if needed in the future. The main problem with it now is the
 * styled-system props abstraction which a lot of images depends atm.
 */
import isPropValid from '@emotion/is-prop-valid';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import Head from 'next/head';
import PropTypes from 'prop-types';
import { forwardRef } from 'react';
import {
  border,
  compose,
  flexbox,
  layout,
  position,
  space,
  system,
} from 'styled-system';

const StyledImage = styled('img', {
  shouldForwardProp: (prop) =>
    isPropValid(prop) &&
    !['width', 'maxWidth', 'height', 'display'].includes(prop),
})`
  ${({ grayscale }) =>
    grayscale &&
    css`
      filter: grayscale(1);
    `}
  ${({ objectFit }) =>
    objectFit &&
    css`
      object-fit: ${objectFit};
    `}
  ${compose(border, flexbox, space, layout, position)}
  ${system({
    opacity: {
      property: 'opacity',
      defaultScale: ['0', '.1', '.25', '.35', '.50', '.75', '1'],
    },
  })}
`;

const Image = forwardRef(
  ({ priority, loading, src, srcSet, sizes, ...rest }, ref) => {
    const ImageComponent = (
      <StyledImage
        ref={ref}
        loading={loading}
        src={src}
        srcSet={srcSet}
        sizes={sizes}
        {...rest}
      />
    );

    if (priority) {
      return (
        <>
          <Head>
            <link
              rel="preload"
              as="image"
              href={srcSet && sizes ? undefined : src}
              imagesrcset={srcSet}
              imagesizes={sizes}
              // `key` is a unique prop from the Next.js API.
              // Is is used on components inside `Head` to skip
              // possible duplicates, so we don't preload the same image
              // twice if the image is used and preloaded more than once.
              // See: https://nextjs.org/docs/api-reference/next/head
              key={srcSet && sizes ? srcSet : src}
            />
          </Head>
          {ImageComponent}
        </>
      );
    }

    return ImageComponent;
  }
);

Image.displayName = 'Image';

Image.defaultProps = {
  grayscale: false,
  objectFit: null,
  loading: 'lazy',
  srcSet: undefined,
  sizes: undefined,
  priority: false,
};

Image.propTypes = {
  grayscale: PropTypes.bool,
  // See options: https://developer.mozilla.org/en-US/docs/Web/CSS/object-fit
  objectFit: PropTypes.oneOf([
    'contain',
    'cover',
    'fill',
    'none',
    'scale-down',
  ]),
  loading: PropTypes.oneOf(['eager', 'lazy']),
  src: PropTypes.string.isRequired,
  srcSet: PropTypes.string,
  sizes: PropTypes.string,
  priority: PropTypes.bool,
};

export default Image;
